/**
 * Kommentare unter eigenen Beitraegen (ab 0.17.0): lesen, beantworten,
 * ausblenden — je Beitrag ein Block, neueste Aktivitaet oben.
 *
 * Geantwortet wird oeffentlich, im Namen des Unternehmens. Bei medizinnahen
 * Sites prueft die Engine die Antwort vorher wie einen Beitrag; was nicht
 * besteht, wird nicht gesendet, die Befunde stehen am Kommentar.
 * „Ausblenden“ statt loeschen: Der Verfasser sieht seinen Kommentar weiter,
 * alle anderen nicht — das verhindert den naechsten, wuetenderen.
 */

import { useState } from "react";

import type { HubKommentarFaden } from "../core/types.js";
import { useSocialKommentare } from "./hooks.js";
import { fehlerText, KANAL } from "./Planung.js";

export interface SocialKommentareProps {
  basePath?: string;
  userName?: string;
}

function wann(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function SocialKommentare({ basePath, userName = "" }: SocialKommentareProps) {
  const k = useSocialKommentare(basePath);
  const [info, setInfo] = useState<string | null>(null);

  if (k.loading && !k.data) return <p className="ch-muted">Lädt …</p>;
  if (k.error) return <p className="ch-hinweis">Die Kommentare konnten nicht geladen werden: {k.error}</p>;
  const daten = k.data;
  if (!daten) return null;

  return (
    <div className="ch-kommentare">
      <div className="ch-kopf">
        <span className="ch-muted">{daten.neu > 0 ? `${daten.neu} neu` : "Nichts Neues"}</span>
        <span className="ch-aktionen" style={{ margin: 0 }}>
          {daten.neu > 0 && (
            <button type="button" className="ch-knopf" onClick={() => void k.gelesen()}>Alle als gelesen</button>
          )}
          <button type="button" className="ch-knopf"
            onClick={() => void k.abrufen().then(() => setInfo("Wird abgerufen — in einer Minute neu laden."))}>
            Jetzt abrufen
          </button>
        </span>
      </div>
      {info && <p className="ch-muted ch-klein">{info}</p>}
      {daten.neu_verbinden.length > 0 && (
        <p className="ch-hinweis">
          Für Kommentare bitte {daten.neu_verbinden.map((p) => KANAL[p] ?? p).join(" und ")} unter „Konten“ neu
          verbinden — beim ersten Verbinden war diese Berechtigung noch nicht dabei.
        </p>
      )}
      {daten.beitraege.length === 0 && (
        <p className="ch-muted">
          Noch keine Kommentare. Angezeigt werden Kommentare unter Beiträgen, die automatisch gepostet wurden —
          30 Tage lang.
        </p>
      )}
      {daten.beitraege.map((g) => (
        <section key={g.post.id} className="ch-plan">
          <div className="ch-kopf">
            <span>
              <strong>{KANAL[g.post.channel] ?? g.post.channel}</strong>
              <span className="ch-muted"> · {g.post.caption.split("\n")[0]?.slice(0, 80)}</span>
            </span>
            {g.post.posted_url && (
              <a className="ch-klein" href={g.post.posted_url} target="_blank" rel="noopener noreferrer">zum Beitrag</a>
            )}
          </div>
          <ul className="ch-karten">
            {g.kommentare.map((f) => <Faden key={f.id} faden={f} k={k} userName={userName} />)}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Faden({ faden: f, k, userName }: { faden: HubKommentarFaden; k: ReturnType<typeof useSocialKommentare>;
  userName: string }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<{ text: string; befunde: string[] } | null>(null);

  async function tun(arbeit: () => Promise<void>) {
    setBusy(true);
    setFehler(null);
    try {
      await arbeit();
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="ch-karte" style={f.hidden ? { opacity: 0.6 } : undefined}>
      <div className="ch-kopf">
        <span><strong>{f.author || "Jemand"}</strong> <span className="ch-muted ch-klein">{wann(f.written_at)}</span></span>
        <span className={f.status === "neu" ? "ch-plakette ch-plakette-warn" : f.status === "beantwortet"
          ? "ch-plakette ch-plakette-ok" : "ch-plakette"}>
          {f.hidden ? "ausgeblendet" : f.status}
        </span>
      </div>
      <p className="ch-text" style={{ whiteSpace: "pre-line" }}>{f.text}</p>
      {f.antworten.map((a) => (
        <p key={a.id} className="ch-text ch-klein" style={{ marginLeft: 16, whiteSpace: "pre-line" }}>
          <strong>{a.from_us ? `Sie${a.answered_by ? ` (${a.answered_by})` : ""}` : a.author}:</strong> {a.text}
        </p>
      ))}

      {offen ? (
        <form onSubmit={(e) => {
          e.preventDefault();
          void tun(async () => {
            await k.antworten(f.id, text, userName);
            setText("");
            setOffen(false);
          });
        }}>
          <textarea className="ch-feld" rows={3} value={text} onChange={(e) => setText(e.target.value)}
            maxLength={1000} placeholder="Ihre Antwort — öffentlich sichtbar" autoFocus />
          <div className="ch-aktionen">
            <button type="submit" className="ch-knopf ch-knopf-primaer" disabled={busy || !text.trim()}>Antworten</button>
            <button type="button" className="ch-knopf" disabled={busy}
              onClick={() => void tun(async () => setText((await k.vorschlag(f.id)).text))}>Antwort vorschlagen</button>
            <button type="button" className="ch-knopf" disabled={busy} onClick={() => setOffen(false)}>Abbrechen</button>
          </div>
        </form>
      ) : (
        <div className="ch-aktionen">
          <button type="button" className="ch-knopf" onClick={() => setOffen(true)}>Antworten</button>
          <button type="button" className="ch-knopf" disabled={busy}
            onClick={() => void tun(() => k.ausblenden(f.id, !f.hidden))}>
            {f.hidden ? "Wieder einblenden" : "Ausblenden"}
          </button>
          {f.status === "neu" && (
            <button type="button" className="ch-knopf" disabled={busy}
              onClick={() => void tun(() => k.gelesen([f.id]))}>Gelesen</button>
          )}
        </div>
      )}
      {fehler && (
        <div className="ch-hinweis">
          {fehler.befunde.length > 0 ? (
            <>
              <strong>So nicht sendbar (Heilmittelwerbegesetz):</strong>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{fehler.befunde.map((b) => <li key={b}>{b}</li>)}</ul>
            </>
          ) : fehler.text}
        </div>
      )}
    </li>
  );
}
